import { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { DollarSign } from 'lucide-react';
import { APP_CHART, APP_CHART_SEQUENCE } from '../../lib/chartTheme';
import type { Account } from '../../types';

interface Props {
  accounts: Account[];
  title?: string;
}

const INDUSTRY_KEYWORDS: [string, string[]][] = [
  ['Financial Services', ['financial services', 'banking', 'insurance', 'asset management', 'fintech', 'hedge fund', 'investment', 'brokerage', 'wealth management', 'capital markets']],
  ['Technology', ['software', 'saas', 'technology', 'information technology', 'platform', 'cloud', 'ai ', 'machine learning', 'data analytics']],
  ['Healthcare & Life Sciences', ['healthcare', 'hospital', 'pharma', 'pharmaceutical', 'biotech', 'healthtech', 'life sciences', 'medical', 'clinical']],
  ['Retail & E-commerce', ['retail', 'e-commerce', 'ecommerce', 'marketplace', 'consumer goods', 'fashion', 'grocery']],
  ['Manufacturing & Industrial', ['manufacturing', 'automotive', 'aerospace', 'industrial', 'heavy industry', 'chemicals', 'materials']],
  ['Telecommunications & Media', ['telecom', 'telecommunications', 'media', 'broadcasting', 'streaming', 'telco', 'entertainment']],
  ['Energy & Utilities', ['energy', 'oil', 'gas', 'renewables', 'utilities', 'power', 'mining']],
  ['Public Sector & Government', ['government', 'public sector', 'defence', 'defense', 'education', 'university', 'non-profit', 'nonprofit', 'ngo', 'military']],
  ['Transportation & Logistics', ['transportation', 'logistics', 'shipping', 'airline', 'aviation', 'freight', 'supply chain', 'trucking', 'rail']],
  ['Professional Services', ['consulting', 'legal', 'accounting', 'agency', 'professional services', 'advisory', 'law firm', 'audit']],
];

const TIER_COLORS: Record<string, string> = {
  A: APP_CHART.brand,
  B: APP_CHART.focus,
  C: APP_CHART.accent,
  Untiered: APP_CHART.muted,
};

const INDUSTRY_COLORS = APP_CHART_SEQUENCE;

function normalizeIndustry(raw: string | null | undefined): string {
  if (!raw) return 'Other';
  const lower = raw.toLowerCase();
  for (const [canonical, keywords] of INDUSTRY_KEYWORDS) {
    for (const kw of keywords) {
      if (lower.includes(kw)) return canonical;
    }
  }
  return 'Other';
}

function formatTam(val: number): string {
  if (val >= 1e9) return `$${(val / 1e9).toFixed(1)}B`;
  if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(1)}K`;
  return `$${val.toFixed(0)}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function tamTooltipFormatter(value: any) {
  return formatTam(Number(value) || 0);
}

export default function TamBreakdownCard({ accounts, title = 'TAM Breakdown' }: Props) {
  const validAccounts = useMemo(
    () => accounts.filter(a => a.overview_tam_max && a.overview_tam_max > 0),
    [accounts],
  );

  const totalTam = useMemo(
    () => validAccounts.reduce((sum, a) => sum + a.overview_tam_max, 0),
    [validAccounts],
  );

  const industryData = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of validAccounts) {
      const ind = normalizeIndustry(a.industry);
      map.set(ind, (map.get(ind) || 0) + a.overview_tam_max);
    }
    const sorted = Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
    if (sorted.length <= 8) {
      return sorted.map(([name, tam]) => ({ name, tam }));
    }
    const top8 = sorted.slice(0, 8);
    const otherTam = sorted.slice(8).reduce((sum, [, v]) => sum + v, 0);
    return [...top8.map(([name, tam]) => ({ name, tam })), { name: 'Other', tam: otherTam }];
  }, [validAccounts]);

  const tierData = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of validAccounts) {
      const tier = a.tier && a.tier.trim() ? a.tier.toUpperCase() : 'Untiered';
      map.set(tier, (map.get(tier) || 0) + a.overview_tam_max);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, tam]) => ({ name: `Tier ${name}`.replace('Tier Untiered', 'Untiered'), tam }));
  }, [validAccounts]);

  if (validAccounts.length === 0) {
    return (
      <div className="bg-app-surface rounded-lg border border-app-line p-6">
        <h3 className="text-sm font-semibold text-app-ink mb-4">{title}</h3>
        <div className="text-center py-10">
          <DollarSign className="w-8 h-8 text-app-line-strong mx-auto mb-2" />
          <p className="text-sm text-app-muted">No TAM data available yet. TAM appears once accounts have TAM values.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-app-surface rounded-lg border border-app-line overflow-hidden">
      <div className="px-6 py-5 border-b border-app-line">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-app-ink">{title}</h3>
            <p className="text-xs text-app-muted mt-0.5">{validAccounts.length} accounts with TAM data</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-app-muted uppercase tracking-wider">Total TAM</p>
            <p className="text-2xl font-bold text-app-ink">{formatTam(totalTam)}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-app-line">
        <div className="p-6">
          <h4 className="text-xs font-semibold text-app-muted uppercase tracking-wider mb-4">TAM by Industry</h4>
          <ResponsiveContainer width="100%" height={Math.max(200, industryData.length * 32)}>
            <BarChart data={industryData} layout="vertical" margin={{ left: 0, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={APP_CHART.line} horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 10, fill: APP_CHART.muted }}
                tickFormatter={formatTam}
              />
              <YAxis
                dataKey="name"
                type="category"
                width={140}
                tick={{ fontSize: 10, fill: APP_CHART.muted }}
              />
              <Tooltip
                formatter={tamTooltipFormatter}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${APP_CHART.line}`, backgroundColor: APP_CHART.surface }}
                labelStyle={{ fontWeight: 600 }}
              />
              <Bar dataKey="tam" radius={[0, 4, 4, 0]} barSize={18}>
                {industryData.map((_, i) => (
                  <Cell key={i} fill={INDUSTRY_COLORS[i % INDUSTRY_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="p-6">
          <h4 className="text-xs font-semibold text-app-muted uppercase tracking-wider mb-4">TAM by Tier</h4>
          <ResponsiveContainer width="100%" height={Math.max(200, tierData.length * 48)}>
            <BarChart data={tierData} layout="vertical" margin={{ left: 0, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={APP_CHART.line} horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 10, fill: APP_CHART.muted }}
                tickFormatter={formatTam}
              />
              <YAxis
                dataKey="name"
                type="category"
                width={80}
                tick={{ fontSize: 11, fill: APP_CHART.muted, fontWeight: 600 }}
              />
              <Tooltip
                formatter={tamTooltipFormatter}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${APP_CHART.line}`, backgroundColor: APP_CHART.surface }}
                labelStyle={{ fontWeight: 600 }}
              />
              <Bar dataKey="tam" radius={[0, 4, 4, 0]} barSize={24}>
                {tierData.map((entry) => {
                  const tierKey = entry.name.replace('Tier ', '');
                  return (
                    <Cell key={entry.name} fill={TIER_COLORS[tierKey] || APP_CHART.muted} />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <div className="mt-4 flex flex-wrap gap-3">
            {tierData.map(entry => {
              const tierKey = entry.name.replace('Tier ', '');
              const pct = totalTam > 0 ? ((entry.tam / totalTam) * 100).toFixed(1) : '0';
              return (
                <div key={entry.name} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: TIER_COLORS[tierKey] || APP_CHART.muted }} />
                  <span className="text-[10px] text-app-muted">{entry.name}: {formatTam(entry.tam)} ({pct}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
